import { useEffect, useMemo, useRef, useState } from "react";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  FolderInput,
  ListFilter,
  ListRestart,
  MailCheck,
  Pencil,
  RefreshCw,
  Search,
  SquarePen,
  Trash2,
  X,
} from "lucide-react";
import type { FilterDto, MessageSummaryDto, VirtualFolderDto } from "../../../shared/types";
import { MessageList, avatarFor } from "./MessageList";
import { MessageListItem } from "./MessageListItem";
import { AnalyzeTab } from "./AnalyzeTab";
import { SearchTab } from "./SearchTab";
import { ReadingPane } from "./ReadingPane";
import { CalendarDashboard } from "../calendar/CalendarDashboard";
import { ComposeDialog } from "./ComposeDialog";
import { SearchDialog, type SearchSubmit } from "./SearchDialog";
import { RunFiltersDialog } from "./RunFiltersDialog";
import { AddToAiGroupDialog } from "./AddToAiGroupDialog";
import { ConfirmDialog } from "../common/ConfirmDialog";
import {
  useAccounts,
  useAiSearch,
  useBulkDeleteMessages,
  useCategorize,
  useDeleteMessage,
  useFilters,
  useInfiniteMessages,
  useLabels,
  useMarkRead,
  useSearchMessages,
  useSenderFilterMessages,
  useSenderGroups,
  useSync,
  useAssignToVirtualFolder,
  useDeleteVirtualFolder,
  useUnassignFromVirtualFolder,
  useVirtualFolderMessages,
  useVirtualFolders,
} from "../../hooks/useApi";
import { OTHER_FILTER_ID, SWATCH_CLASSES } from "../filterColors";
import { splitCombinedId } from "./dragMessage";

interface SearchResult {
  label: string;
  messages: MessageSummaryDto[];
}

export function MailView({
  accountId,
  folderId = null,
  selectedFilter,
  hiddenAccountIds,
  runFiltersOpen,
  onCloseRunFilters,
  selectedSenderFilter,
  onClearSenderFilter,
  selectedVirtualFolder,
  onClearVirtualFolder,
  onEditVirtualFolder,
  onCreateFilterFromSender,
  onCreateFolderFromSender,
}: {
  accountId: number;
  folderId?: string | null;
  selectedFilter: number | null;
  hiddenAccountIds: Set<number>;
  /** "Run filters (AI)" dialog, triggered from the sidebar. */
  runFiltersOpen: boolean;
  onCloseRunFilters: () => void;
  /** Active sender-filter (server search over history); null = none. */
  selectedSenderFilter: number | null;
  onClearSenderFilter: () => void;
  /** Active virtual folder (DB-only custom folder); null = none. */
  selectedVirtualFolder: number | null;
  onClearVirtualFolder: () => void;
  onEditVirtualFolder: (folder: VirtualFolderDto) => void;
  onCreateFilterFromSender: (address: string) => void;
  /** "Create folder" in the reading pane: new-folder dialog prefilled with the sender. */
  onCreateFolderFromSender: (address: string) => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [panelTab, setPanelTab] = useState<"inbox" | "suggestions">("inbox");
  // Mail panel width: drag the divider to resize; persisted across sessions.
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = Number(localStorage.getItem("mail.panelWidth"));
    return saved >= 280 && saved <= 640 ? saved : 390;
  });
  const startPanelResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = panelWidth;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    const move = (ev: MouseEvent) => {
      const w = Math.min(640, Math.max(280, startW + ev.clientX - startX));
      setPanelWidth(w);
      localStorage.setItem("mail.panelWidth", String(w));
    };
    const up = () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };
  const [contentTab, setContentTab] = useState<"dashboard" | "email" | "analyze" | "search">(
    "dashboard",
  );
  const [selectMode, setSelectMode] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [pinTarget, setPinTarget] = useState<MessageSummaryDto | null>(null);
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const queryClient = useQueryClient();
  const sync = useSync(accountId);
  // Spinner tracks the list fetches only — long background syncs are shown in
  // the SyncProgressPanel instead of spinning this button for minutes.
  const refreshing = useIsFetching({ queryKey: ["messages", accountId] }) > 0;
  const { data: labels } = useLabels(accountId);
  const { data: filters } = useFilters();
  const { data: accounts } = useAccounts();
  const { data: messagesData } = useInfiniteMessages(accountId, folderId);
  const categorize = useCategorize(accountId, folderId);
  const searchMessages = useSearchMessages(accountId);
  const aiSearch = useAiSearch(accountId, folderId);
  const deleteMessage = useDeleteMessage(accountId);
  // Folder rows always carry combined "accountId:messageId" ids (user-level
  // folders span accounts), so their deletes go through the All-view hook.
  const deleteCombinedRow = useDeleteMessage(0);
  const [pendingRowDelete, setPendingRowDelete] = useState<{
    message: MessageSummaryDto;
    combined: boolean;
  } | null>(null);
  const bulkDeleteMessages = useBulkDeleteMessages(accountId);
  const { data: senderGroups } = useSenderGroups();
  // Runtime range for the active sender-filter. null = fast default (recent 50).
  const [senderRange, setSenderRange] = useState<number | null>(null);
  useEffect(() => setSenderRange(null), [selectedSenderFilter]); // reset per filter
  const senderFilterQuery = useSenderFilterMessages(accountId, selectedSenderFilter, senderRange);
  const activeSenderFilter = (senderGroups ?? [])
    .flatMap((g) => g.filters)
    .find((f) => f.id === selectedSenderFilter);
  // Virtual folder view (DB-only custom folders, always combined ids).
  const vfolderQuery = useVirtualFolderMessages(selectedVirtualFolder);
  // Needed for the active folder view AND the "Move to folder" menu.
  const { data: virtualFolders } = useVirtualFolders(selectedVirtualFolder != null || selectMode);
  const assignToFolder = useAssignToVirtualFolder();
  const markRead = useMarkRead(accountId);
  // Folder rows carry combined "accountId:raw" ids — those go through account 0.
  const markCombinedRead = useMarkRead(0);
  // Selected-mail actions dropdown: top level or the move-to-folder submenu.
  const [actionMenu, setActionMenu] = useState<"actions" | "folders" | null>(null);
  const busyBulk = assignToFolder.isPending;
  useEffect(() => {
    if (!actionMenu) return;
    const close = () => setActionMenu(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [actionMenu]);
  const activeVirtualFolder = (virtualFolders ?? []).find((f) => f.id === selectedVirtualFolder);
  const unassignFromFolder = useUnassignFromVirtualFolder();
  const deleteVirtualFolder = useDeleteVirtualFolder();
  const [vfolderDeleteOpen, setVfolderDeleteOpen] = useState(false);

  const loadedMessageIds = useMemo(
    () => [...new Set((messagesData?.pages ?? []).flatMap((page) => page.messages.map((message) => message.id)))],
    [messagesData],
  );

  // Flattened pages for the Analyze tab (deduped ids happen above; the tab
  // filters hidden accounts and re-sorts itself).
  const loadedMessages = useMemo(
    () => (messagesData?.pages ?? []).flatMap((p) => p.messages),
    [messagesData],
  );

  // The unread funnel applies in EVERY view (inbox, folder, sender, search).
  const onlyUnread = (list: MessageSummaryDto[]) =>
    unreadOnly ? list.filter((m) => !m.isRead) : list;

  // Ids of every message shown in the active view (sender-filter / search /
  // folder list, with the same narrowing MessageList applies). Backs "Select all".
  const visibleMessageIds = useMemo(() => {
    if (selectedVirtualFolder != null) {
      return onlyUnread(vfolderQuery.data?.messages ?? []).map((m) => m.id);
    }
    if (selectedSenderFilter != null) {
      return onlyUnread(senderFilterQuery.data?.messages ?? []).map((m) => m.id);
    }
    if (searchResult) return onlyUnread(searchResult.messages).map((m) => m.id);
    const msgs = (messagesData?.pages ?? []).flatMap((p) => p.messages);
    return msgs
      .filter((m) => {
        if (m.origin && hiddenAccountIds.has(m.origin.accountId)) return false;
        if (unreadOnly && m.isRead) return false;
        if (selectedFilter === null) return true;
        const matched = labels?.[m.id] ?? [];
        if (selectedFilter === OTHER_FILTER_ID) return matched.length === 0;
        return matched.includes(selectedFilter);
      })
      .map((m) => m.id);
  }, [
    selectedVirtualFolder,
    vfolderQuery.data,
    selectedSenderFilter,
    senderFilterQuery.data,
    searchResult,
    messagesData,
    labels,
    selectedFilter,
    hiddenAccountIds,
    unreadOnly,
  ]);
  const allVisibleSelected =
    visibleMessageIds.length > 0 && visibleMessageIds.every((id) => checkedIds.has(id));

  // Distinct senders from the loaded pages, for the search dialog's quick-pick.
  const senders = useMemo(() => {
    if (!searchOpen) return [];
    const byAddress = new Map<string, { name: string; address: string }>();
    for (const page of messagesData?.pages ?? []) {
      for (const m of page.messages) {
        if (m.from?.address) byAddress.set(m.from.address.toLowerCase(), m.from);
      }
    }
    return [...byAddress.values()].sort((a, b) =>
      (a.name || a.address).localeCompare(b.name || b.address),
    );
  }, [searchOpen, messagesData]);

  const handleSearch = async (submit: SearchSubmit) => {
    try {
      if (submit.mode === "ai") {
        const messages = await aiSearch.mutateAsync(submit.prompt);
        setSearchResult({ label: `AI: "${submit.prompt.slice(0, 50)}"`, messages });
      } else {
        const parts: string[] = [];
        if (submit.criteria.from) parts.push(`from: ${submit.criteria.from}`);
        if (submit.criteria.subject) parts.push(`subject: ${submit.criteria.subject}`);
        if (submit.criteria.body) parts.push(`body: ${submit.criteria.body}`);

        let messages: MessageSummaryDto[];
        if (parts.length) {
          messages = (await searchMessages.mutateAsync(submit.criteria)).messages;
        } else {
          // Badge-only search runs over the currently loaded pages.
          messages = messagesData?.pages.flatMap((p) => p.messages) ?? [];
        }
        if (submit.badgesOn) {
          const on = new Set(submit.badgesOn);
          messages = messages.filter((m) => {
            const matched = labels?.[m.id] ?? [];
            if (matched.length === 0) return on.has(OTHER_FILTER_ID);
            return matched.some((filterId) => on.has(filterId));
          });
          const names = (filters ?? [])
            .filter((f: FilterDto) => on.has(f.id))
            .map((f: FilterDto) => f.name);
          if (on.has(OTHER_FILTER_ID)) names.push("Other");
          parts.push(`badge: ${names.join("/") || "none"}`);
        }
        setSearchResult({ label: parts.join(" · "), messages });
      }
      setSearchOpen(false);
    } catch {
      // The mutation error is surfaced inside the dialog.
    }
  };

  const toggleChecked = (id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const bulkDelete = () => {
    setBulkConfirmOpen(false);
    setBulkError(null);
    const ids = [...checkedIds];
    // Count unread BEFORE the request — the delete hook prunes these rows
    // from the view caches immediately, so they can't be counted afterwards.
    const unreadSel = selectedVirtualFolder != null ? unreadAmong(new Set(ids)) : 0;
    // Optimistic: rows vanish and the selection clears right away; the server
    // delete runs in the background (one request over a single connection —
    // avoids the per-message reconnect storm that broke IMAP bulk deletes).
    if (selectedId && ids.includes(selectedId)) setSelectedId(null);
    setCheckedIds(new Set());
    if (selectedVirtualFolder != null) {
      bumpFolderCounts(selectedVirtualFolder, { total: -ids.length, unread: -unreadSel });
    }
    bulkDeleteMessages.mutate(ids, {
      onSuccess: (res) => {
        if (res.failed.length) {
          // The failed rows come back with the hook's refetch; re-check them
          // so the user can see and retry exactly what's left.
          setCheckedIds(new Set(res.failed));
          setBulkError(
            `${res.failed.length} of ${ids.length} emails could not be deleted — they are back in the list.`,
          );
        }
      },
      onError: (err) =>
        setBulkError(`Delete failed: ${err.message} — the emails are back in the list.`),
      onSettled: () => refreshFolderCounts(),
    });
  };

  // Refresh = force a provider sync (new mail lands in the local store and
  // is auto-categorized), then the useSync polling refreshes the views.
  const refresh = () => {
    sync.mutate({ force: true });
    queryClient.invalidateQueries({ queryKey: ["messages", accountId] });
    queryClient.invalidateQueries({ queryKey: ["stats", accountId] });
  };

  // Opening a message flips the content area to the Email tab so the click
  // is never a no-op while the Dashboard tab is showing.
  const selectMessage = (id: string) => {
    rangeAnchor.current = id;
    setSelectedId(id);
    setContentTab("email");
  };

  // Analyze-tab cards: folder columns carry combined "acc:raw" ids (user-level
  // folders span accounts). ReadingPane only splits those in the All view
  // (accountId 0), so normalize here for per-account views.
  const openFromAnalyze = (id: string) => {
    const sep = id.indexOf(":");
    if (accountId === 0 || sep < 0) return selectMessage(id);
    const acc = Number(id.slice(0, sep));
    if (acc === accountId) return selectMessage(id.slice(sep + 1));
    // Mail from another account can't open in this account's view — no-op.
  };

  // Shift+click selects the whole range between the last clicked mail and this
  // one (entering select mode if needed) — for bulk delete or drag-to-folder.
  const rangeAnchor = useRef<string | null>(null);

  // A selection never survives a view change — otherwise "451 selected" from
  // the inbox would silently carry into a folder and bulk actions would hit
  // mail the user can no longer see.
  useEffect(() => {
    setCheckedIds(new Set());
    rangeAnchor.current = null;
  }, [selectedVirtualFolder, selectedSenderFilter, selectedFilter, searchResult, accountId, folderId]);
  const clickMessage = (id: string, e?: React.MouseEvent) => {
    if (!e?.shiftKey) return selectMessage(id);
    e.preventDefault();
    const list = visibleMessageIds;
    const to = list.indexOf(id);
    if (to < 0) return selectMessage(id);
    const from = rangeAnchor.current ? list.indexOf(rangeAnchor.current) : -1;
    const [lo, hi] = from < 0 ? [to, to] : [Math.min(from, to), Math.max(from, to)];
    setSelectMode(true);
    setCheckedIds((prev) => {
      const next = new Set(prev);
      for (let i = lo; i <= hi; i++) next.add(list[i]!);
      return next;
    });
  };

  // Sidebar folder unread counts must reflect a bulk action immediately —
  // don't wait for the 60s background refetch.
  const refreshFolderCounts = () => {
    void queryClient.refetchQueries({ queryKey: ["virtual-folders"] });
    void queryClient.refetchQueries({ queryKey: ["virtual-folder-messages"] });
  };

  // Instant sidebar feedback: patch the cached folder counts right away (the
  // refetch corrects any drift — this matters when the DB round trip is slow).
  const bumpFolderCounts = (folderId: number, delta: { unread?: number; total?: number }) => {
    queryClient.setQueryData<VirtualFolderDto[]>(["virtual-folders"], (old) =>
      old?.map((f) =>
        f.id === folderId
          ? {
              ...f,
              unreadCount: Math.max(0, f.unreadCount + (delta.unread ?? 0)),
              totalCount: Math.max(0, f.totalCount + (delta.total ?? 0)),
            }
          : f,
      ),
    );
  };

  // Messages backing the current view (folder / sender / search / inbox).
  const activeList = (): MessageSummaryDto[] =>
    selectedVirtualFolder != null
      ? vfolderQuery.data?.messages ?? []
      : selectedSenderFilter != null
        ? senderFilterQuery.data?.messages ?? []
        : searchResult
          ? searchResult.messages
          : (messagesData?.pages ?? []).flatMap((p) => p.messages);

  /** How many of `ids` are unread in the current view's data. */
  const unreadAmong = (ids: Set<string>) =>
    activeList().filter((m) => ids.has(m.id) && !m.isRead).length;

  // Move every checked mail into a virtual folder ("Move to folder…" action).
  const moveChecked = (folderId: number) => {
    setActionMenu(null);
    const ids = new Set(checkedIds);
    const messages = [...ids].map((id) => splitCombinedId(id, accountId));
    if (!messages.length) return;
    const unread = unreadAmong(ids);
    assignToFolder.mutate(
      { folderId, messages },
      {
        onSuccess: () => {
          setCheckedIds(new Set());
          bumpFolderCounts(folderId, { total: messages.length, unread });
          if (selectedVirtualFolder != null && selectedVirtualFolder !== folderId) {
            bumpFolderCounts(selectedVirtualFolder, { total: -messages.length, unread: -unread });
          }
          refreshFolderCounts();
        },
      },
    );
  };

  // Mark the checked mail as read. Only the unread ones are sent — "Select
  // all" typically drags in hundreds of already-read mails that need no
  // request at all. Optimistic: rows flip and the selection clears instantly
  // (the hook patches every cache in onMutate); requests run in the background.
  const markAllRead = () => {
    setActionMenu(null);
    setBulkError(null);
    const ids = activeList()
      .filter((m) => checkedIds.has(m.id) && !m.isRead)
      .map((m) => m.id);
    setCheckedIds(new Set());
    if (!ids.length) return;
    void (async () => {
      const results = await Promise.allSettled(ids.map((id) => markRead.mutateAsync(id)));
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed) {
        setBulkError(
          `${failed} of ${ids.length} emails could not be marked as read — they show unread again.`,
        );
      }
      refreshFolderCounts();
    })();
  };

  // After a delete, jump to the next mail in the visible list (or the previous
  // one when the deleted mail was last) so triage keeps flowing.
  const selectNextAfter = (deletedId: string) => {
    const i = visibleMessageIds.indexOf(deletedId);
    setSelectedId(visibleMessageIds[i + 1] ?? visibleMessageIds[i - 1] ?? null);
  };

  // Row trash icon in the folder / sender / search views (the inbox list has
  // its own confirm inside MessageList).
  const runRowDelete = () => {
    const p = pendingRowDelete;
    if (!p) return;
    // Optimistic: the hook prunes the row immediately — close the dialog,
    // advance the selection and patch counts now; the delete runs in the
    // background and a failure restores the row with an error banner.
    setPendingRowDelete(null);
    selectNextAfter(p.message.id);
    if (selectedVirtualFolder != null) {
      bumpFolderCounts(selectedVirtualFolder, {
        total: -1,
        unread: p.message.isRead ? 0 : -1,
      });
      refreshFolderCounts();
    }
    (p.combined ? deleteCombinedRow : deleteMessage).mutate(p.message.id, {
      onError: (err) =>
        setBulkError(`Delete failed: ${err.message} — the email is back in the list.`),
    });
  };

  const iconButton =
    "grid size-7 place-items-center rounded text-ink-muted hover:bg-black/5 hover:text-accent disabled:opacity-50";

  return (
    <div className="flex h-full">
      <div
        className="flex shrink-0 flex-col border-r border-stroke bg-white"
        style={{ width: panelWidth }}
      >
        <div className="flex h-9 shrink-0 items-center border-b border-stroke pl-1.5 pr-1.5">
          <div className="flex flex-1 items-stretch self-stretch">
            {(["inbox", "suggestions"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setPanelTab(tab)}
                className={`relative px-2.5 text-[13px] ${
                  panelTab === tab ? "font-semibold text-accent" : "text-ink-muted hover:text-ink"
                }`}
              >
                {tab === "inbox" ? "Inbox" : "Suggestions"}
                {panelTab === tab && (
                  <span className="absolute inset-x-1.5 bottom-0 h-[2px] rounded-t bg-accent" />
                )}
              </button>
            ))}
          </div>
        </div>

        {panelTab === "suggestions" ? (
          <div className="grid flex-1 place-items-center">
            <p className="px-6 text-center text-[13px] text-ink-muted">
              No suggestions yet.
            </p>
          </div>
        ) : (
          <>
            <div className="flex h-8 shrink-0 items-center border-b border-stroke px-1.5">
              <div className="flex flex-1 items-center justify-end gap-0.5">
                <button
                  onClick={refresh}
                  disabled={refreshing}
                  title="Sync mail now"
                  aria-label="Sync mail now"
                  className={iconButton}
                >
                  <RefreshCw
                    size={15}
                    strokeWidth={2}
                    className={refreshing ? "animate-spin" : ""}
                  />
                </button>
                <button
                  onClick={() => sync.mutate({ force: true, reconcile: true })}
                  disabled={sync.isPending}
                  title="Deep sync — remove mail deleted in other apps and pick up moves"
                  aria-label="Deep sync"
                  className={iconButton}
                >
                  <ListRestart
                    size={15}
                    strokeWidth={2}
                    className={sync.isPending ? "animate-pulse" : ""}
                  />
                </button>
                <button
                  onClick={() => setSearchOpen(true)}
                  title="Search mail"
                  aria-label="Search mail"
                  className={iconButton}
                >
                  <Search size={15} strokeWidth={2} />
                </button>
                <button
                  onClick={() => setComposing(true)}
                  title="New mail"
                  aria-label="New mail"
                  className={iconButton}
                >
                  <SquarePen size={15} strokeWidth={2} />
                </button>
              </div>
            </div>

            {accountId === 0 && (accounts ?? []).length > 0 && (
              <div className="shrink-0 border-b border-stroke bg-surface px-3 py-1.5">
                <div className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={selectMode}
                    onChange={(e) => {
                      setSelectMode(e.target.checked);
                      if (!e.target.checked) setCheckedIds(new Set());
                    }}
                    title="Select multiple emails"
                    aria-label="Select multiple emails"
                    className="mr-0.5 size-4 accent-accent"
                  />
                  <span className="text-[12px] text-ink-muted">Select multiple</span>
                  {selectMode && (
                    <label className="flex items-center gap-1.5 pl-2 text-[12px] text-ink-muted">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={(e) =>
                          setCheckedIds(e.target.checked ? new Set(visibleMessageIds) : new Set())
                        }
                        disabled={visibleMessageIds.length === 0}
                        title="Select all emails in this view"
                        aria-label="Select all emails in this view"
                        className="size-4 accent-accent"
                      />
                      Select all
                    </label>
                  )}
                  <div className="ml-auto flex items-center gap-1.5">
                    {unreadOnly && (
                      <span className="text-[12px] font-medium text-accent">
                        Unread {visibleMessageIds.length}
                      </span>
                    )}
                    <button
                      onClick={() => setUnreadOnly((v) => !v)}
                      title={
                        unreadOnly ? "Showing unread only — click to show all" : "Show unread only"
                      }
                      aria-label="Toggle unread-only filter"
                      className={`grid size-6 place-items-center rounded ${
                        unreadOnly
                          ? "bg-accent text-white hover:bg-accent-hover"
                          : "text-ink-muted hover:bg-black/5 hover:text-accent"
                      }`}
                    >
                      <ListFilter size={15} />
                    </button>
                  </div>
                </div>
                {/* Selection actions get their own row — the top row stays uncramped
                    even when "446 selected" and its dropdown are showing. */}
                {selectMode && checkedIds.size > 0 && (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <div className="relative" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setActionMenu((v) => (v ? null : "actions"))}
                        disabled={busyBulk}
                        className="flex items-center gap-1 rounded bg-accent px-2.5 py-0.5 text-[11px] font-medium text-white hover:bg-accent-hover disabled:opacity-50"
                        title="Actions for the selected emails"
                      >
                        {assignToFolder.isPending ? "Moving…" : `${checkedIds.size} selected`}
                        <ChevronDown size={12} />
                      </button>
                      {actionMenu === "actions" && (
                        <div className="absolute left-0 top-full z-30 mt-1 w-48 rounded border border-stroke bg-white py-1 shadow-lg">
                          <button
                            onClick={() => void markAllRead()}
                            disabled={unreadAmong(checkedIds) === 0}
                            className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[12px] hover:bg-black/5 disabled:opacity-50"
                          >
                            <MailCheck size={13} className="shrink-0 text-ink-muted" />
                            {unreadAmong(checkedIds) === 0
                              ? "No unread selected"
                              : `Mark ${unreadAmong(checkedIds)} unread as read`}
                          </button>
                          <button
                            onClick={() => setActionMenu("folders")}
                            className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[12px] hover:bg-black/5"
                          >
                            <FolderInput size={13} className="shrink-0 text-ink-muted" />
                            Move {checkedIds.size} to folder…
                          </button>
                          <span className="my-1 block border-t border-stroke" />
                          <button
                            onClick={() => {
                              setActionMenu(null);
                              setBulkConfirmOpen(true);
                            }}
                            className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[12px] text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={13} className="shrink-0" />
                            Delete {checkedIds.size} selected
                          </button>
                        </div>
                      )}
                      {actionMenu === "folders" && (
                        <div className="absolute left-0 top-full z-30 mt-1 max-h-64 w-44 overflow-y-auto rounded border border-stroke bg-white py-1 shadow-lg">
                          {(virtualFolders ?? []).map((f) => (
                            <button
                              key={f.id}
                              onClick={() => moveChecked(f.id)}
                              className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[12px] hover:bg-black/5"
                            >
                              <span
                                className={`size-2.5 shrink-0 rounded-full ${SWATCH_CLASSES[f.color]}`}
                              />
                              <span className="truncate">{f.name}</span>
                            </button>
                          ))}
                          {(virtualFolders ?? []).length === 0 && (
                            <p className="px-2.5 py-1.5 text-[12px] text-ink-muted">
                              No folders yet — add one with + in the sidebar.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {bulkConfirmOpen && (
                  <ConfirmDialog
                    title={`Delete ${checkedIds.size} emails?`}
                    message={`${checkedIds.size} selected email${
                      checkedIds.size === 1 ? "" : "s"
                    } will be moved to Trash.`}
                    confirmLabel={`Delete ${checkedIds.size}`}
                    onConfirm={bulkDelete}
                    onCancel={() => setBulkConfirmOpen(false)}
                  />
                )}
              </div>
            )}
            {bulkError && (
              <div className="flex shrink-0 items-center gap-2 border-b border-stroke bg-red-50 px-3 py-1.5">
                <p className="min-w-0 flex-1 text-[12px] text-red-700">{bulkError}</p>
                <button
                  onClick={() => setBulkError(null)}
                  aria-label="Dismiss error"
                  className="grid size-5 shrink-0 place-items-center rounded text-red-700 hover:bg-red-100"
                >
                  <X size={13} />
                </button>
              </div>
            )}

            {selectedVirtualFolder != null ? (
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="flex shrink-0 items-center gap-1.5 border-b border-stroke bg-accent-subtle px-3 py-1.5 text-[12px]">
                  {activeVirtualFolder && (
                    <span
                      className={`size-2.5 shrink-0 rounded-full ${SWATCH_CLASSES[activeVirtualFolder.color]}`}
                    />
                  )}
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-semibold">{activeVirtualFolder?.name ?? "Folder"}</span>
                    {" — "}
                    {vfolderQuery.isLoading
                      ? "loading…"
                      : `${vfolderQuery.data?.messages.length ?? 0} emails`}
                  </span>
                  {activeVirtualFolder && (
                    <button
                      onClick={() => onEditVirtualFolder(activeVirtualFolder)}
                      title="Rename folder"
                      aria-label="Rename folder"
                      className="grid size-5 shrink-0 place-items-center rounded text-ink-muted hover:bg-black/10 hover:text-ink"
                    >
                      <Pencil size={13} />
                    </button>
                  )}
                  <button
                    onClick={() => setVfolderDeleteOpen(true)}
                    title="Delete folder"
                    aria-label="Delete folder"
                    className="grid size-5 shrink-0 place-items-center rounded text-ink-muted hover:bg-black/10 hover:text-red-600"
                  >
                    <Trash2 size={13} />
                  </button>
                  <button
                    onClick={onClearVirtualFolder}
                    title="Close folder"
                    aria-label="Close folder"
                    className="grid size-5 shrink-0 place-items-center rounded text-ink-muted hover:bg-black/10 hover:text-ink"
                  >
                    <X size={13} />
                  </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto">
                  {vfolderQuery.isLoading && (
                    <p className="px-4 py-6 text-center text-[13px] text-ink-muted">Loading…</p>
                  )}
                  {vfolderQuery.data && vfolderQuery.data.messages.length === 0 && (
                    <p className="px-4 py-6 text-center text-[13px] text-ink-muted">
                      Empty — drag mail from the inbox onto this folder.
                    </p>
                  )}
                  {onlyUnread(vfolderQuery.data?.messages ?? []).map((message) => (
                    <MessageListItem
                      key={message.id}
                      message={message}
                      selected={message.id === selectedId}
                      matchedFilters={(labels?.[message.id] ?? [])
                        .map((id) => (filters ?? []).find((f: FilterDto) => f.id === id))
                        .filter((f): f is FilterDto => f !== undefined)}
                      avatar={avatarFor(message, accounts ?? [], accountId)}
                      onClick={(e) => clickMessage(message.id, e)}
                      onAddToAiGroup={setPinTarget}
                      onDelete={() => setPendingRowDelete({ message, combined: true })}
                      onMarkRead={() => markCombinedRead.mutate(message.id)}
                      onUnassign={() => {
                        const sep = message.id.indexOf(":");
                        unassignFromFolder.mutate({
                          folderId: selectedVirtualFolder!,
                          accountId: message.origin?.accountId ?? Number(message.id.slice(0, sep)),
                          messageId: sep > 0 ? message.id.slice(sep + 1) : message.id,
                        });
                      }}
                      dragAccountId={0}
                      dragIds={selectMode && checkedIds.has(message.id) ? [...checkedIds] : undefined}
                      selectMode={selectMode}
                      checked={checkedIds.has(message.id)}
                      onToggleChecked={() => toggleChecked(message.id)}
                    />
                  ))}
                </div>
                {vfolderDeleteOpen && activeVirtualFolder && (
                  <ConfirmDialog
                    title="Delete folder?"
                    message={`"${activeVirtualFolder.name}" will be deleted. Its mail returns to the inbox — nothing is deleted from your mailbox.`}
                    confirmLabel="Delete folder"
                    busy={deleteVirtualFolder.isPending}
                    onConfirm={() =>
                      deleteVirtualFolder.mutate(activeVirtualFolder.id, {
                        onSuccess: () => {
                          setVfolderDeleteOpen(false);
                          onClearVirtualFolder();
                        },
                      })
                    }
                    onCancel={() => setVfolderDeleteOpen(false)}
                  />
                )}
              </div>
            ) : selectedSenderFilter != null ? (
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="flex shrink-0 items-center gap-1.5 border-b border-stroke bg-accent-subtle px-3 py-1.5 text-[12px]">
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-semibold">{activeSenderFilter?.name ?? "Filter"}</span>
                    {" — "}
                    {senderFilterQuery.isLoading
                      ? "searching…"
                      : `${senderFilterQuery.data?.messages.length ?? 0} from ${
                          activeSenderFilter?.addresses.join(", ") ?? "sender"
                        }`}
                  </span>
                  <select
                    value={senderRange ?? "recent"}
                    onChange={(e) =>
                      setSenderRange(e.target.value === "recent" ? null : Number(e.target.value))
                    }
                    title="Time range"
                    aria-label="Time range"
                    className="shrink-0 rounded border border-stroke bg-white px-1.5 py-0.5 text-[12px] outline-none focus:border-accent"
                  >
                    <option value="recent">All</option>
                    <option value="1">Last month</option>
                    <option value="3">3 months</option>
                    <option value="6">6 months</option>
                    <option value="12">12 months</option>
                  </select>
                  <button
                    onClick={onClearSenderFilter}
                    title="Clear filter"
                    aria-label="Clear filter"
                    className="grid size-5 shrink-0 place-items-center rounded text-ink-muted hover:bg-black/10 hover:text-ink"
                  >
                    <X size={13} />
                  </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto">
                  {senderFilterQuery.isLoading && (
                    <p className="px-4 py-6 text-center text-[13px] text-ink-muted">Searching…</p>
                  )}
                  {senderFilterQuery.error && (
                    <p className="px-4 py-6 text-center text-[13px] text-red-700">
                      {senderFilterQuery.error.message}
                    </p>
                  )}
                  {senderFilterQuery.data && senderFilterQuery.data.messages.length === 0 && (
                    <p className="px-4 py-6 text-center text-[13px] text-ink-muted">
                      No mail from this sender in the selected period.
                    </p>
                  )}
                  {onlyUnread(senderFilterQuery.data?.messages ?? []).map((message) => (
                    <MessageListItem
                      key={message.id}
                      message={message}
                      selected={message.id === selectedId}
                      matchedFilters={(labels?.[message.id] ?? [])
                        .map((id) => (filters ?? []).find((f: FilterDto) => f.id === id))
                        .filter((f): f is FilterDto => f !== undefined)}
                      avatar={avatarFor(message, accounts ?? [], accountId)}
                      onClick={(e) => clickMessage(message.id, e)}
                      onAddToAiGroup={setPinTarget}
                      onDelete={() => setPendingRowDelete({ message, combined: false })}
                      onMarkRead={() => markRead.mutate(message.id)}
                      dragAccountId={accountId}
                      dragIds={selectMode && checkedIds.has(message.id) ? [...checkedIds] : undefined}
                      selectMode={selectMode}
                      checked={checkedIds.has(message.id)}
                      onToggleChecked={() => toggleChecked(message.id)}
                    />
                  ))}
                </div>
              </div>
            ) : searchResult ? (
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="flex shrink-0 items-center gap-1.5 border-b border-stroke bg-accent-subtle px-3 py-1.5 text-[12px]">
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-semibold">{searchResult.messages.length}</span> results —{" "}
                    {searchResult.label}
                  </span>
                  <button
                    onClick={() => setSearchResult(null)}
                    title="Clear search"
                    aria-label="Clear search"
                    className="grid size-5 place-items-center rounded text-ink-muted hover:bg-black/10 hover:text-ink"
                  >
                    <X size={13} />
                  </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto">
                  {searchResult.messages.length === 0 && (
                    <p className="px-4 py-6 text-center text-[13px] text-ink-muted">
                      No messages match.
                    </p>
                  )}
                  {onlyUnread(searchResult.messages).map((message) => (
                    <MessageListItem
                      key={message.id}
                      message={message}
                      selected={message.id === selectedId}
                      matchedFilters={(labels?.[message.id] ?? [])
                        .map((id) => (filters ?? []).find((f: FilterDto) => f.id === id))
                        .filter((f): f is FilterDto => f !== undefined)}
                      avatar={avatarFor(message, accounts ?? [], accountId)}
                      onClick={(e) => clickMessage(message.id, e)}
                      onCreateFilterFromSender={(from) => onCreateFilterFromSender(from.address)}
                      onAddToAiGroup={setPinTarget}
                      onDelete={() => setPendingRowDelete({ message, combined: false })}
                      onMarkRead={() => markRead.mutate(message.id)}
                      dragAccountId={accountId}
                      dragIds={selectMode && checkedIds.has(message.id) ? [...checkedIds] : undefined}
                      selectMode={selectMode}
                      checked={checkedIds.has(message.id)}
                      onToggleChecked={() => toggleChecked(message.id)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <MessageList
                accountId={accountId}
                folderId={folderId}
                selectedId={selectedId}
                onSelect={clickMessage}
                labels={labels ?? {}}
                filters={filters ?? []}
                accounts={accounts ?? []}
                selectedFilter={selectedFilter}
                hiddenAccountIds={hiddenAccountIds}
                unreadOnly={unreadOnly}
                selectMode={selectMode}
                checkedIds={checkedIds}
                onToggleChecked={toggleChecked}
                onCreateFilterFromSender={(from) => onCreateFilterFromSender(from.address)}
                onAddToAiGroup={setPinTarget}
                onDeleted={(id) => {
                  if (selectedId === id) selectNextAfter(id);
                }}
              />
            )}
          </>
        )}
      </div>
      {/* Drag handle between the mail panel and the content area. */}
      <div
        onMouseDown={startPanelResize}
        title="Drag to resize"
        className="-ml-[3px] w-[5px] shrink-0 cursor-col-resize bg-transparent transition-colors hover:bg-accent/40 active:bg-accent/60"
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-9 shrink-0 items-stretch border-b border-stroke bg-white pl-1.5">
          {(["dashboard", "email", "analyze", "search"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setContentTab(tab)}
              className={`relative px-2.5 text-[13px] ${
                contentTab === tab ? "font-semibold text-accent" : "text-ink-muted hover:text-ink"
              }`}
            >
              {{
                dashboard: "Calendar",
                email: "Email",
                analyze: "Analyze emails",
                search: "Search emails",
              }[tab]}
              {contentTab === tab && (
                <span className="absolute inset-x-1.5 bottom-0 h-[2px] rounded-t bg-accent" />
              )}
            </button>
          ))}
        </div>
        <div className="flex min-h-0 flex-1">
          {contentTab === "dashboard" ? (
            <CalendarDashboard accountId={accountId} />
          ) : contentTab === "search" ? (
            <SearchTab onOpenMessage={openFromAnalyze} />
          ) : contentTab === "analyze" ? (
            <AnalyzeTab
              accountId={accountId}
              filters={filters ?? []}
              labels={labels ?? {}}
              accounts={accounts ?? []}
              messages={loadedMessages}
              hiddenAccountIds={hiddenAccountIds}
              selectedId={selectedId}
              onOpenMessage={openFromAnalyze}
            />
          ) : (
            <ReadingPane
              accountId={accountId}
              messageId={selectedId}
              onMarkedRead={() => setSelectedId(null)}
              onDeleted={() => selectedId && selectNextAfter(selectedId)}
              onForwarded={() => selectedId && selectNextAfter(selectedId)}
              onCreateFolderFromSender={onCreateFolderFromSender}
              onMovedToFolder={() => selectedId && selectNextAfter(selectedId)}
            />
          )}
        </div>
      </div>
      {composing && (
        <ComposeDialog
          accountId={accountId}
          accounts={accountId === 0 ? accounts : undefined}
          onClose={() => setComposing(false)}
        />
      )}
      {searchOpen && (
        <SearchDialog
          filters={filters ?? []}
          senders={senders}
          searching={searchMessages.isPending || aiSearch.isPending}
          error={searchMessages.error?.message ?? aiSearch.error?.message ?? null}
          onSubmit={handleSearch}
          onClose={() => setSearchOpen(false)}
        />
      )}
      {pendingRowDelete && (
        <ConfirmDialog
          title="Delete email?"
          message={`"${pendingRowDelete.message.subject}" from ${
            pendingRowDelete.message.from?.name ?? "unknown sender"
          } will be moved to Trash.`}
          onConfirm={runRowDelete}
          onCancel={() => setPendingRowDelete(null)}
        />
      )}
      {pinTarget && (
        <AddToAiGroupDialog
          message={pinTarget}
          accountId={accountId}
          onClose={() => setPinTarget(null)}
        />
      )}
      {runFiltersOpen && (
        <RunFiltersDialog
          filters={filters ?? []}
          messageIds={loadedMessageIds}
          onRun={(filterIds) => categorize.mutateAsync(filterIds)}
          onClose={onCloseRunFilters}
        />
      )}
    </div>
  );
}
